import { jsPDF } from "jspdf";
import { api } from "@/lib/api";
import { getTheme } from "@/lib/mediaKitThemes";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const NF = { green: [17, 138, 96], ink: [24, 24, 27], sub: [113, 113, 122], line: [228, 228, 231], soft: [244, 246, 245] };

const PLATFORM_LABEL = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", twitter: "X / Twitter", facebook: "Facebook", linkedin: "LinkedIn" };

async function loadImage(url) {
  if (!url) return null;
  try {
    const abs = url.startsWith("http") || url.startsWith("data:") ? url : `${BACKEND}${url}`;
    // Route through the authed api client for /api/files, else fetch directly.
    if (abs.includes("/api/files/")) {
      const path = abs.replace(BACKEND, "").replace(/^\/api/, "");
      const res = await api.get(path, { responseType: "blob" });
      return await blobToImg(res.data);
    }
    const res = await fetch(abs, { mode: "cors" });
    if (!res.ok) return null;
    return await blobToImg(await res.blob());
  } catch { return null; }
}
function blobToImg(blob) {
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => {
      const img = new Image();
      img.onload = () => resolve({ dataUrl: fr.result, w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = fr.result;
    };
    fr.onerror = () => resolve(null);
    fr.readAsDataURL(blob);
  });
}

// filename helper
export function mediaKitFilename(name) {
  const safe = (name || "Creator").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "Creator";
  return `${safe}_Media_Kit.pdf`;
}

export async function generateMediaKitPdf(kit) {
  const ACCENT = getTheme(kit.theme).rgb;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 44;
  const CW = PW - M * 2;
  let y = M;

  const need = (h) => { if (y + h > PH - 54) { footer(); doc.addPage(); y = M; } };
  const footer = () => {
    doc.setDrawColor(...NF.line); doc.setLineWidth(0.5);
    doc.line(M, PH - 40, PW - M, PH - 40);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...NF.sub);
    doc.text("Powered by Nearfluence", M, PH - 26);
    const p = `Page ${doc.internal.getNumberOfPages()}`;
    doc.text(p, PW - M - doc.getTextWidth(p), PH - 26);
  };
  const heading = (t) => {
    need(34); doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...ACCENT);
    doc.text(t.toUpperCase(), M, y); y += 8;
    doc.setDrawColor(...ACCENT); doc.setLineWidth(1.4); doc.line(M, y, M + 40, y); y += 16;
  };
  const para = (t, opts = {}) => {
    const size = opts.size || 10; const color = opts.color || NF.ink; const gap = opts.gap != null ? opts.gap : 14;
    doc.setFont("helvetica", opts.bold ? "bold" : "normal"); doc.setFontSize(size); doc.setTextColor(...color);
    const lines = doc.splitTextToSize(t || "", opts.w || CW);
    lines.forEach((ln) => { need(gap); doc.text(ln, opts.x != null ? opts.x : M, y); y += gap; });
  };
  const link = (label, url, x, yy) => {
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...ACCENT);
    doc.textWithLink(label, x, yy, { url });
  };

  // ---- Accent band + Header (photo + name + tagline + location) ----
  doc.setFillColor(...ACCENT); doc.rect(0, 0, PW, 6, "F");
  const photo = kit.profile_photo ? await loadImage(kit.profile_photo) : null;
  const headerH = 96;
  need(headerH);
  if (photo) {
    const size = 74;
    // circle-ish: draw rounded via addImage square with a light frame
    doc.setFillColor(...NF.soft); doc.roundedRect(M, y, size, size, 10, 10, "F");
    try { doc.addImage(photo.dataUrl, "JPEG", M + 2, y + 2, size - 4, size - 4, undefined, "FAST"); } catch { /* skip */ }
  }
  const tx = photo ? M + 90 : M;
  const savedY = y;
  doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(...NF.ink);
  doc.text(kit.display_name || "Creator", tx, y + 22);
  let hy = y + 40;
  if (kit.tagline) { doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(...ACCENT); doc.text(doc.splitTextToSize(kit.tagline, CW - (tx - M)), tx, hy); hy += 16; }
  const meta = [kit.location, (kit.niches || []).slice(0, 4).join(" · ")].filter(Boolean).join("  •  ");
  if (meta) { doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...NF.sub); doc.text(doc.splitTextToSize(meta, CW - (tx - M)), tx, hy); hy += 14; }
  y = Math.max(savedY + headerH, hy + 8);
  doc.setDrawColor(...NF.line); doc.setLineWidth(0.5); doc.line(M, y, PW - M, y); y += 22;

  // ---- About ----
  if (kit.bio) { heading("About"); para(kit.bio, { gap: 13 }); y += 8; }

  // ---- Platforms & socials ----
  const socials = kit.socials || {};
  const socialKeys = Object.keys(socials);
  if (socialKeys.length) {
    heading("Platforms & audience");
    socialKeys.forEach((k) => {
      const s = socials[k] || {};
      need(30);
      doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(...NF.ink);
      doc.text(PLATFORM_LABEL[k] || k, M, y);
      const handle = s.username ? `@${String(s.username).replace(/^@/, "")}` : "";
      if (handle) { const hx = M + 120; if (s.url) link(handle, s.url, hx, y); else { doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...NF.sub); doc.text(handle, hx, y); } }
      y += 14;
      const stats = [];
      if (s.followers) stats.push(`${fmtNum(s.followers)} followers`);
      if (s.avg_views) stats.push(`${fmtNum(s.avg_views)} avg views`);
      if (s.engagement) stats.push(`${s.engagement}% engagement`);
      if (stats.length) {
        const src = s.source === "instagram" || s.source === "connected" || s.source === "verified" ? "Connected" : "Creator-provided";
        para(`${stats.join("  ·  ")}   (${src}${s.last_updated ? `, updated ${String(s.last_updated).slice(0, 10)}` : ""})`, { size: 9, color: NF.sub, gap: 13, x: M + 8, w: CW - 8 });
      }
      y += 4;
    });
    y += 6;
  }

  // ---- Manual / creator-provided stats ----
  if ((kit.manual_stats || []).length) {
    heading("Highlights");
    (kit.manual_stats || []).forEach((st) => { if (st.label || st.value) para(`${st.label}: ${st.value}  (creator-provided)`, { gap: 13 }); });
    y += 8;
  }

  // ---- Services ----
  if ((kit.services || []).length) {
    heading("Services");
    (kit.services || []).forEach((s) => {
      const price = s.price_from != null && s.price_from !== "" ? `  —  from £${s.price_from}` : "";
      para(`• ${labelService(s.type)}${price}`, { gap: 14 });
    });
    if (kit.gifted_ok) para("• Open to gifted / product collaborations", { gap: 14 });
    y += 6;
  }

  // ---- Rates ----
  if (kit.rates_on_request || kit.rates_text) {
    heading("Rates");
    para(kit.rates_text && !kit.rates_on_request ? kit.rates_text : "Rates available on request.", { gap: 14 });
    y += 6;
  }

  // ---- Portfolio ----
  const portfolio = (kit.portfolio || []).filter((p) => p && (p.url || p.thumbnail));
  if (portfolio.length) {
    heading("Portfolio");
    const cols = 3; const gap = 12; const cell = (CW - gap * (cols - 1)) / cols; const cellH = cell;
    for (let i = 0; i < portfolio.length; i++) {
      const col = i % cols;
      if (col === 0) need(cellH + 24);
      const px = M + col * (cell + gap);
      const item = portfolio[i];
      const src = item.thumbnail || (item.type === "image" ? item.url : "");
      const img = src ? await loadImage(src) : null;
      doc.setFillColor(...NF.soft); doc.roundedRect(px, y, cell, cellH, 8, 8, "F");
      if (img) { try { doc.addImage(img.dataUrl, "JPEG", px, y, cell, cellH, undefined, "FAST"); } catch { /* skip */ } }
      else { doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...NF.sub); doc.text(item.type === "video" ? "Video" : "Link", px + 8, y + 16); }
      if (item.type === "video") { doc.setFillColor(255, 255, 255); doc.circle(px + cell / 2, y + cellH / 2, 10, "F"); doc.setTextColor(...ACCENT); doc.setFontSize(11); doc.text(">", px + cell / 2 - 2.5, y + cellH / 2 + 4); }
      if (item.url) doc.link(px, y, cell, cellH, { url: item.url.startsWith("http") ? item.url : `${BACKEND}${item.url}` });
      if (col === cols - 1 || i === portfolio.length - 1) y += cellH + 12;
    }
    y += 6;
  }

  // ---- Previous collaborations ----
  if ((kit.collaborations || []).length) {
    heading("Previous collaborations");
    for (const c of kit.collaborations) {
      if (!c || !c.brand) continue;
      need(40);
      const img = c.image ? await loadImage(c.image) : null;
      const ix = M; const textX = img ? M + 52 : M;
      if (img) { doc.setFillColor(...NF.soft); doc.roundedRect(ix, y - 8, 44, 44, 6, 6, "F"); try { doc.addImage(img.dataUrl, "JPEG", ix, y - 8, 44, 44, undefined, "FAST"); } catch { /* */ } }
      doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(...NF.ink);
      doc.text(c.brand + (c.date ? `  ·  ${c.date}` : ""), textX, y);
      y += 13;
      if (c.description) para(c.description, { size: 9.5, color: NF.sub, gap: 12, x: textX, w: CW - (textX - M) });
      if (c.url) { link("View content →", c.url, textX, y); y += 14; }
      para("Creator-provided", { size: 8, color: NF.sub, gap: 12, x: textX });
      y += 6;
    }
    y += 4;
  }

  // ---- Testimonials ----
  if ((kit.testimonials || []).length) {
    heading("Testimonials");
    (kit.testimonials || []).forEach((t) => {
      if (!t || !t.quote) return;
      para(`“${t.quote}”`, { gap: 13 });
      if (t.author || t.role) para(`— ${[t.author, t.role].filter(Boolean).join(", ")}`, { size: 9, color: NF.sub, gap: 13 });
      y += 6;
    });
  }

  // ---- Contact (only if published) ----
  if (kit.contact_email) { heading("Contact"); link(kit.contact_email, `mailto:${kit.contact_email}`, M, y); y += 16; }

  footer();
  doc.save(mediaKitFilename(kit.display_name));
}

function fmtNum(n) {
  n = Number(n) || 0;
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}
function labelService(t) {
  const m = { ugc: "UGC creation", posted: "Content posted to my audience", both: "UGC + posted content", event: "Event coverage", photography: "Photography", video: "Video production" };
  return m[t] || t || "Service";
}
